import React, { useState, useEffect } from "react";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Mail,
  Search,
  Calendar,
  Clock,
  XCircle,
  Loader,
  Ban,
  CheckCircle,
} from "lucide-react";

const RequestMail = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  
  // Checkbox selection state
  const [selectedData, setSelectedData] = useState([]);
  const [enableOptions, setEnableOptions] = useState(false);
  
  const navigate = useNavigate();
  const apiAddress = process.env.REACT_APP_SECRET;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    loadRestaurantData();
    fetchEmails();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const loadRestaurantData = async () => {
    try {
      const token = localStorage.getItem("gotasteadmintoken");
      var decodedData = {};
      if (token != null) {
        try {
          decodedData = jwt_decode(token);
        } catch (error) {
          console.error("Token decode error:", error);
        }
      }
      const restaurant = JSON.parse(localStorage.getItem("restaurant"));

      if (!decodedData.UserId || !restaurant?.id) {
        console.error("Missing UserId or restaurant ID");
        return;
      }

      const response = await axios.get(
        apiAddress + "/api/Restaurant/getrestaurantdetailsbyidfortable/" +
        decodedData.UserId + "/" + restaurant.id
      );

      setRestaurants(response.data || []);
    } catch (error) {
      console.error("Error loading restaurants:", error);
    }
  };

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiAddress}/api/SubSection/GetAllEmail`);
      const data = await res.json();
      setEmails(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    return restaurant ? restaurant.name : "Unknown Restaurant";
  };

  const fetchEmailById = async (emailId) => {
    try {
      setShowModal(true);
      setLoadingEmail(true);
      setSelectedEmail(null);

      const res = await fetch(`${apiAddress}/api/SubSection/GetEmailById/${emailId}`);
      if (!res.ok) {
        console.error('Failed to fetch email by id', res.status);
        setSelectedEmail(null);
        return;
      }
      const data = await res.json();
      setSelectedEmail(data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEmail(false);
    }
  };

  const openUpdateCommunication = (emailId) => {
    navigate(`/update-communication/${emailId}`);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (emailId) => {
    setSelectedData(prev => {
      const newSelection = prev.includes(emailId)
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId];
      
      setEnableOptions(newSelection.length > 0);
      return newSelection;
    });
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedEmails.map(email => email.id);
      setSelectedData(allIds);
      setEnableOptions(true);
    } else {
      setSelectedData([]);
      setEnableOptions(false);
    }
  };

  // Action handlers
    const handleApprove = async () => {
    if (selectedData.length === 0) {
      toast.warning("Please select at least one email");
      return;
    }

    // If any selected email is already approved, warn and abort
    const alreadyApproved = selectedData.some((id) => {
      const email = emails.find((e) => e.id === id);
      return email && email.statuss === 2;
    });
    if (alreadyApproved) {
      toast.warning("One or more selected emails are already approved");
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            textAlign: "center",
            minWidth: "400px"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              backgroundColor: "#d4edda",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}>
              <CheckCircle size={32} color="#28a745" />
            </div>
            <h1 style={{ margin: "0 0 10px 0", fontSize: "24px", fontWeight: "600" }}>
              Are you sure?
            </h1>
            <p style={{ margin: "0 0 30px 0", color: "#6c757d", fontSize: "16px" }}>
              You want to approve {selectedData.length} email(s)?
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button 
                onClick={onClose}
                style={{
                  padding: "12px 30px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                No
              </button>
              <button
                onClick={() => {
                  approveEmails();
                  onClose();
                }}
                style={{
                  padding: "12px 30px",
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Yes, Approve
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const approveEmails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("gotasteadmintoken");
      
      // Approve each selected email
      const updatePromises = selectedData.map(async (emailId) => {
        const email = emails.find(e => e.id === emailId);
        if (!email) return;
        
        const payload = {
          id: emailId,
          restaurantId: email.restaurantId || "",
          sub: email.sub || "",
          body: email.body || "",
          statuss: 2, // 2 = Approved
          creationId: email.creationId || "admin",
        };
        
        return axios.put(
          `${apiAddress}/api/SubSection/UpdateEmail`,
          payload,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
          }
        );
      });
      
      await Promise.all(updatePromises);
      
      toast.success("Successfully Approved!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
      
      setSelectedData([]);
      setEnableOptions(false);
      fetchEmails();
    } catch (error) {
      console.error("Error approving emails:", error);
      toast.error(error.response?.data?.message || "Failed to approve emails", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleReject = async () => {
    if (selectedData.length === 0) {
      toast.warning("Please select at least one email");
      return;
    }
    
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            textAlign: "center",
            minWidth: "400px"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              backgroundColor: "#f8d7da",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}>
              <Ban size={32} color="#dc3545" />
            </div>
            <h1 style={{ margin: "0 0 10px 0", fontSize: "24px", fontWeight: "600" }}>
              Are you sure?
            </h1>
            <p style={{ margin: "0 0 30px 0", color: "#6c757d", fontSize: "16px" }}>
              You want to reject {selectedData.length} email(s)?
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button 
                onClick={onClose}
                style={{
                  padding: "12px 30px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                No
              </button>
              <button
                onClick={() => {
                  rejectEmails();
                  onClose();
                }}
                style={{
                  padding: "12px 30px",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Yes, Reject
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const rejectEmails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("gotasteadmintoken");
      
      // Reject each selected email
      const updatePromises = selectedData.map(async (emailId) => {
        const email = emails.find(e => e.id === emailId);
        if (!email) return;
        
        const payload = {
          id: emailId,
          restaurantId: email.restaurantId || "",
          sub: email.sub || "",
          body: email.body || "",
          statuss: 3, // 3 = Rejected
          creationId: email.creationId || "admin",
        };
        
        return axios.put(
          `${apiAddress}/api/SubSection/UpdateEmail`,
          payload,
          {
            headers: {
              Authorization: "Bearer " + token,
            },
          }
        );
      });
      
      await Promise.all(updatePromises);
      
      toast.success("Successfully Rejected!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
      
      setSelectedData([]);
      setEnableOptions(false);
      fetchEmails();
    } catch (error) {
      console.error("Error rejecting emails:", error);
      toast.error(error.response?.data?.message || "Failed to reject emails", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter with restaurant name search
  const filteredEmails = emails.filter(
    (e) =>
      e.sub?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.body?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getRestaurantName(e.restaurantId)?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEmails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmails = filteredEmails.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Count emails by status
  const allMails = emails.length;
  const pendingMails = emails.filter(e => e.statuss === 0 || e.statuss === 1).length;
  const approvedMails = emails.filter(e => e.statuss === 2).length;
  const rejectedMails = emails.filter(e => e.statuss === 3).length;

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
      case 1:
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor: "#ffc107",
            color: "#000"
          }}>
            <Clock size={14} /> Pending
          </span>
        );
      case 2:
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor: "#28a745",
            color: "#fff"
          }}>
            <CheckCircle size={14} /> Approved
          </span>
        );
      case 3:
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor: "#dc3545",
            color: "#fff"
          }}>
            <Ban size={14} /> Rejected
          </span>
        );
      default:
        return <span>Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center", 
        height: "80vh" 
      }}>
        <Loader size={48} style={{ animation: "spin 1s linear infinite" }} />
        <p>Loading emails...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "40px", backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer />
      
      {/* Dashboard Cards */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "24px",
        margin: "32px 0"
      }}>
        <div style={{
          backgroundColor: "rgba(157, 220, 243, 0.266)",
          color: "#000",
          borderRadius: "12px",
          padding: "40px",
          minWidth: "200px",
          textAlign: "center"
        }}>
          <p style={{ fontSize: "48px", fontWeight: "700", margin: "0 0 8px 0" }}>
            {allMails}
          </p>
          <p style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>
            All Mails
          </p>
        </div>

        <div style={{
          backgroundColor: "rgba(249, 197, 138, 0.302)",
          color: "#000",
          borderRadius: "12px",
          padding: "40px",
          minWidth: "200px",
          textAlign: "center"
        }}>
          <p style={{ fontSize: "48px", fontWeight: "700", margin: "0 0 8px 0" }}>
            {pendingMails}
          </p>
          <p style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>
            Pending Mails
          </p>
        </div>

        <div style={{
          backgroundColor: "rgba(164, 208, 111, 0.364)",
          color: "#000",
          borderRadius: "12px",
          padding: "40px",
          minWidth: "200px",
          textAlign: "center"
        }}>
          <p style={{ fontSize: "48px", fontWeight: "700", margin: "0 0 8px 0" }}>
            {approvedMails}
          </p>
          <p style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>
            Approved Mails
          </p>
        </div>

        <div style={{
          backgroundColor: "rgba(220, 53, 69, 0.3)",
          color: "#000",
          borderRadius: "12px",
          padding: "40px",
          minWidth: "200px",
          textAlign: "center"
        }}>
          <p style={{ fontSize: "48px", fontWeight: "700", margin: "0 0 8px 0" }}>
            {rejectedMails}
          </p>
          <p style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>
            Rejected Mails
          </p>
        </div>
      </div>

      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Mail size={32} color="#007bff" />
          <div>
            <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "600" }}>Email Requests</h1>
            <p style={{ margin: 0, color: "#6c757d" }}>All email requests from restaurants</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: "flex",
        gap: "12px",
        marginBottom: "20px",
        alignItems: "center"
      }}>
        <button
          onClick={handleApprove}
          disabled={!enableOptions}
          style={{
            padding: "10px 20px",
            backgroundColor: enableOptions ? "#28a745" : "#e9ecef",
            color: enableOptions ? "#fff" : "#6c757d",
            border: "none",
            borderRadius: "8px",
            cursor: enableOptions ? "pointer" : "not-allowed",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <CheckCircle size={16} /> Approve
        </button>

        <button
          onClick={handleReject}
          disabled={!enableOptions}
          style={{
            padding: "10px 20px",
            backgroundColor: enableOptions ? "#dc3545" : "#e9ecef",
            color: enableOptions ? "#fff" : "#6c757d",
            border: "none",
            borderRadius: "8px",
            cursor: enableOptions ? "pointer" : "not-allowed",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <Ban size={16} /> Reject
        </button>

        {selectedData.length > 0 && (
          <span style={{
            padding: "10px 15px",
            backgroundColor: "#007bff",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "500"
          }}>
            {selectedData.length} selected
          </span>
        )}
      </div>

      {/* Search */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        backgroundColor: "#fff", 
        padding: "10px 20px", 
        borderRadius: "8px", 
        marginBottom: "20px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
      }}>
        <Search size={18} color="#6c757d" />
        <input
          style={{ 
            flex: 1, 
            border: "none", 
            outline: "none",
            fontSize: "14px",
            padding: "12px 16px"
          }}
          placeholder="Search by restaurant, subject or content..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button 
          onClick={() => {
            loadRestaurantData();
            fetchEmails();
          }}
          style={{
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ 
        backgroundColor: "#fff", 
        borderRadius: "8px", 
        overflow: "hidden",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ backgroundColor: "#f8f9fa" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={paginatedEmails.length > 0 && selectedData.length === paginatedEmails.length}
                  style={{ cursor: "pointer" }}
                />
              </th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>Restaurant</th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>Subject</th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>Preview</th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "600" }}>Date</th>
            </tr>
          </thead>

          <tbody>
            {filteredEmails.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ 
                  textAlign: "center", 
                  padding: "40px", 
                  color: "#6c757d" 
                }}>
                  No emails found
                </td>
              </tr>
            ) : (
              paginatedEmails.map((email) => (
                <tr key={email.id} style={{ borderBottom: "1px solid #dee2e6" }}>
                  <td style={{ padding: "12px" }}>
                    <input
                      type="checkbox"
                      checked={selectedData.includes(email.id)}
                      onChange={() => handleCheckboxChange(email.id)}
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                  
                  <td style={{ padding: "12px", fontSize: "13px", fontWeight: "500" }}>
                    {(email.restaurantName)}
                  </td>
                  
                  <td style={{ padding: "12px", fontSize: "13px" }}>
                    {email.sub || "No Subject"}
                  </td>

                  <td
                    onClick={() => openUpdateCommunication(email.id)}
                    style={{ 
                      padding: "12px", 
                      fontSize: "13px", 
                      cursor: "pointer",
                      color: "#007bff",
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {email.body?.replace(/<[^>]*>/g, "").slice(0, 90)}...
                  </td>

                  <td style={{ padding: "12px" }}>{getStatusBadge(email.statuss)}</td>

                  <td style={{ padding: "12px", fontSize: "13px", color: "#6c757d" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <Calendar size={14} />
                      {new Date(email.creationDate).toLocaleDateString()}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            padding: "20px",
            gap: "10px"
          }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              style={{
                padding: "8px 16px",
                border: "1px solid #dee2e6",
                backgroundColor: currentPage === 1 ? "#e9ecef" : "#fff",
                borderRadius: "6px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px"
              }}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #dee2e6",
                  backgroundColor: currentPage === i + 1 ? "#007bff" : "#fff",
                  color: currentPage === i + 1 ? "#fff" : "#000",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px"
                }}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              style={{
                padding: "8px 16px",
                border: "1px solid #dee2e6",
                backgroundColor: currentPage === totalPages ? "#e9ecef" : "#fff",
                borderRadius: "6px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px"
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px",
              borderBottom: "1px solid #dee2e6"
            }}>
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                {selectedEmail?.sub || "Email Preview"}
              </h2>
              <XCircle 
                onClick={() => setShowModal(false)} 
                style={{ cursor: "pointer" }}
                size={24}
              />
            </div>

            <div style={{ padding: "20px" }}>
              {loadingEmail ? (
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center",
                  padding: "40px"
                }}>
                  <Loader size={40} style={{ animation: "spin 1s linear infinite" }} />
                  <p>Loading email...</p>
                </div>
              ) : (
                selectedEmail && (
                  <>
                    <div style={{ 
                      display: "flex", 
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "20px"
                    }}>
                      <div>
                        <strong>Restaurant:</strong> {getRestaurantName(selectedEmail.restaurantId)}
                      </div>
                      {getStatusBadge(selectedEmail.statuss)}
                    </div>

                    <div style={{ marginBottom: "15px", color: "#6c757d", fontSize: "14px" }}>
                      {new Date(selectedEmail.creationDate).toLocaleString()}
                    </div>

                    {selectedEmail.sub && (
                      <h3 style={{ 
                        fontSize: "18px", 
                        fontWeight: "600",
                        marginBottom: "15px"
                      }}>
                        {selectedEmail.sub}
                      </h3>
                    )}

                    <div
                      style={{ 
                        padding: "15px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        marginBottom: "20px"
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedEmail.body,
                      }}
                    />
                  </>
                )
              )}
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "20px",
              borderTop: "1px solid #dee2e6"
            }}>
              <button 
                onClick={() => setShowModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RequestMail;